import {
  Bleed,
  Box,
  Button,
  Heading,
  HiddenVisually,
  IconChevron,
  Inline,
  Stack,
} from 'braid-design-system';
import { v4 as uuid } from 'uuid';
import { useAuth } from '../../../shared/providers/authProvider';
import { useFeatureFlags } from '../../../shared/providers/featureFlagProvider';
import { trackViewEvents } from '../../../shared/tracking/candidateApp/trackViewEvents';

import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import { useHubble } from '@seek/hubble';
import {
  ErrorBoundaryContent,
  useAppContext,
  useConfig,
  type Jobs,
} from '@seek/libs-shared';
import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';

import { useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { CompanyProfilesPage, ROOT_PATH } from '../../../paths';
import { sortSalaryRangeByRoles } from '../../../shared/utils/sortSalaryRangeByRoles';
import { useScrollToTop } from '../../../shared/utils/useScrollToTop';
import { CompaniesList } from '../../components/companiesList/CompaniesList';
import { CompanyReviewAISummarySection } from '../../components/salaries/companyReviewAISummarySection/CompanyReviewAISummarySection';
import { OpenJobsSection } from '../../components/salaries/openJobsSection/OpenJobsSection';
import { SalariesByRoleSection } from '../../components/salaries/salariesByRoleSection/SalariesByRolesSection';
import { SalariesListingSection } from '../../components/salaries/salariesListingSection/SalariesListingSection';
import { SalarySatisfactionSection } from '../../components/salaries/salarySatisfactionSection/SalarySatisfactionSection';
import translations from './.vocab';
import type { SalariesTabModel } from './salariesTabModel';

interface SalaryPageByRoleProps {
  getPaginatedJobs: (organisationId: string, page?: number) => Promise<Jobs>;
  model: SalariesTabModel;
  onActiveTabChanged: (item: string | undefined) => void;
  trackJobsPaginationClicked: ({
    searchTotalCount,
    pageNumber,
  }: {
    pageNumber: number;
    searchTotalCount?: number;
  }) => void;
  trackRandomCompanyProfileLinkClicked: ({
    linkedCompanyId,
  }: {
    linkedCompanyId: string;
  }) => void;
  trackReviewsClicked: () => void;
}

export const SalariesPageByRole = ({
  model,
  getPaginatedJobs,
  trackJobsPaginationClicked,
  trackRandomCompanyProfileLinkClicked,
  onActiveTabChanged,
  trackReviewsClicked,
}: SalaryPageByRoleProps) => {
  const { t } = useTranslations(translations);
  const config = useConfig();
  const { companyName, ...context } = model;
  const params = useParams();
  const navigate = useNavigate();
  const { companyId } = useAppContext();
  const { authenticationStatus } = useAuth();
  const hubble = useHubble();
  const [searchParams] = useSearchParams();

  const serviceToken = searchParams.get('serviceToken') ?? undefined;

  const { cookieFeatureFlags } = useFeatureFlags();

  const isIndustrySalariesEnabled =
    cookieFeatureFlags.COMPANY_PROFILES_INDUSTRY_SALARIES;

  const localize = useMelwaysLink();
  const isInitialRender = useRef(
    // When page first load, the default history length is 2
    typeof window !== 'undefined' && window.history.length <= 2,
  );
  const { scrollToTopRef, scrollToTop } = useScrollToTop('PAGE');

  const salaryHubLink = localize({
    path: `/${ROOT_PATH}/${params.companySlug}/${CompanyProfilesPage.Salaries}`,
    language: config.language,
  });

  const navigateBackSalaryHub = () => {
    void navigate(salaryHubLink);
  };

  const roleId = params.roleId;

  useEffect(() => {
    hubble.updateOptions({ pageName: 'company profile' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Prevent scrolling to top on initial render to ensure the company banner
    // remains visible (full view) when the page first loads
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  // Effect for tracking events
  useEffect(() => {
    if (
      companyId &&
      companyName &&
      authenticationStatus !== AUTHENTICATION_TYPES.AUTH_PENDING
    ) {
      const trackingCorrelationId = uuid();
      trackViewEvents.salariesRolePage({
        config,
        context: { ...context, companyId, companyName },
        trackingCorrelationId,
      });

      hubble.trackEvent('company_details_displayed', {
        companyId,
        serviceToken,
        currentPageSection: 'salaries role',
        trackingCorrelationId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companyName, authenticationStatus]);

  if (model.shouldShowErrorPage) {
    return <ErrorBoundaryContent config={config} />;
  }

  const currentRole = model.salaryRanges?.salaryRangeByRoles?.find(
    (role) => role.roleId === roleId,
  );
  const roleName = currentRole?.roleTitle ?? '';

  const salaryRanges = model.salaryRanges;

  // TODO: Phase 1 only shows salary range by company, to be cleanup at phase 2
  const salaryRangeByRoles = salaryRanges?.salaryRangeByRoles.filter(
    (item) => Boolean(item.rangeByCompany) && item.roleId !== params.roleId,
  );

  const sortedSalaryRangeByRoles = sortSalaryRangeByRoles({
    salaryRangeByRoles: salaryRangeByRoles || [],
    language: config.language,
  });

  return (
    <>
      <HiddenVisually>
        <Heading level="1">
          {t('role salary at companyName', {
            roleName,
            companyName,
          })}
        </Heading>
      </HiddenVisually>
      {isIndustrySalariesEnabled && (
        <Box paddingY="medium">
          <Heading level="3">Salary for {roleName}</Heading>
        </Box>
      )}
      <Box ref={scrollToTopRef}>
        <Box paddingTop="small" paddingBottom={['small', 'medium']}>
          <Inline align="left" space={'small'}>
            <Bleed left={'small'}>
              <Button
                tone="neutral"
                variant="transparent"
                onClick={navigateBackSalaryHub}
                size="small"
              >
                <IconChevron direction="left" />
                {t('Back to all companyName salaries', {
                  companyName,
                })}
              </Button>
            </Bleed>
          </Inline>
        </Box>
        <Stack space={'large'}>
          <SalariesByRoleSection model={model} />
          {model.jobsModel.jobs.length > 0 && (
            <OpenJobsSection
              model={model}
              getPaginatedJobs={getPaginatedJobs}
              trackJobsPaginationClicked={trackJobsPaginationClicked}
            />
          )}
          {!!model.salarySatisfactionRating && (
            <SalarySatisfactionSection model={model} />
          )}
          {model.companyReviewsAISummary && (
            <CompanyReviewAISummarySection
              model={model}
              onActiveTabChanged={onActiveTabChanged}
              trackReviewsClicked={trackReviewsClicked}
            />
          )}
          {sortedSalaryRangeByRoles.length >= 1 ? (
            <SalariesListingSection
              salaryRangeByRoles={sortedSalaryRangeByRoles}
              companyName={companyName}
              isSalariesByRole={true}
            />
          ) : null}
          {model.randomCompaniesInThisIndustry &&
            model.randomCompaniesInThisIndustry.length !== 0 && (
              <CompaniesList
                title={t('Other companies in this industry')}
                allCompanies={model.randomCompaniesInThisIndustry}
                trackCompanyProfileLinkClicked={
                  trackRandomCompanyProfileLinkClicked
                }
                isHiddenBelowTablet={false}
              />
            )}
        </Stack>
      </Box>
    </>
  );
};
